import type { ComponentType } from 'react';

// ============================================================================
// contentRegistry.ts — ЕДИНЫЙ РЕЕСТР ОБРАБОТЧИКОВ КОНТЕНТА (SSoT, клиент).
// Обработчик = компонент просмотра/редактирования файла по расширению.
// Регистрация: сначала встроенные (builtinContentHandlers), затем приложения
// МОГУТ ПЕРЕОПРЕДЕЛИТЬ (последний по приоритету выигрывает) — «переназначить
// редактор» (OnlyOffice, свой просмотрщик, etc).
// ============================================================================

export interface ContentHandler {
  id: string;              // уникальный код (напр. 'builtin.xlsx-grid', 'app.onlyoffice.doc')
  label: string;           // человекочитаемое имя («Таблица (грид)»)
  extensions: string[];    // без точки, нижний регистр
  // Компонент: fileId + fileName; может читать данные через sdk (ParseContent/Download).
  view: ComponentType<ContentViewProps>;
  canEdit?: boolean;       // редактор (true) или только просмотр
}

export interface ContentViewProps {
  fileId: string;
  fileName: string;
  fileType?: string;       // mime
  onError?: (message: string) => void;
}

// Порядок = приоритет (первые зарегистрированные встроенные — базовые;
// более поздние переопределяют). resolve возвращает ПОСЛЕДНЕГО (переопределение).
const registry: ContentHandler[] = [];

export function registerContentHandler(h: ContentHandler): void {
  registry.push(h);
}

// Переопределение по id: заменяет существующего с тем же id или добавляет в конец.
export function upsertContentHandler(h: ContentHandler): void {
  const idx = registry.findIndex((x) => x.id === h.id);
  if (idx >= 0) {
    registry[idx] = h;
  } else {
    registry.push(h);
  }
}

// Резолв обработчика по расширению: ПОСЛЕДНИЙ зарегистрированный (переопределение).
export function resolveContentHandler(ext: string): ContentHandler | undefined {
  const e = (ext || '').toLowerCase().replace(/^\./, '');
  for (let i = registry.length - 1; i >= 0; i--) {
    if (registry[i].extensions.includes(e)) {
      return registry[i];
    }
  }
  return undefined;
}

// Все обработчики (для меню «Открыть с помощью…»).
export function listContentHandlers(): ContentHandler[] {
  return [...registry];
}

// Обработчики, умеющие формат (для «Открыть с помощью…»).
export function handlersFor(ext: string): ContentHandler[] {
  const e = (ext || '').toLowerCase().replace(/^\./, '');
  return registry.filter((h) => h.extensions.includes(e));
}

// getHandlerById — обработчик по id (undefined если нет).
export function getHandlerById(id: string): ContentHandler | undefined {
  return registry.find((h) => h.id === id);
}

// resolveWithDefault — обработчик с учётом предпочтения пользователя
// (user_settings content.default_app.{ext}); предпочтение — если обработчик
// существует и умеет формат — побеждает; иначе — последний зарегистрированный.
export function resolveWithDefault(ext: string, preferredId?: string): ContentHandler | undefined {
  if (preferredId) {
    const pref = getHandlerById(preferredId);
    const e = (ext || '').toLowerCase().replace(/^\./, '');
    if (pref && pref.extensions.includes(e)) return pref;
  }
  return resolveContentHandler(ext);
}
